import { useCallback, useState } from "react";
import { Factory } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, Loading, ExportButton } from "@/components/Common";
import { MonthPicker, PastMonthBanner } from "@/components/MonthView";
import { monthStr } from "@/lib/format";
import { FilterBar, useRowFilter } from "@/components/Filters";
import { MovementBadge } from "@/components/Badges";
import { PurchaseOrderDialog } from "@/components/OrderDialogs";
import { useLoad } from "@/hooks/useData";
import { api } from "@/lib/api";

export default function ReorderPlan() {
  const [month, setMonth] = useState(monthStr());
  const { data } = useLoad(() => api.get("/report/products", { params: { month } }).then((r) => r.data), [month]);
  const needs = useCallback((r) => r.reorder_qty > 0, []);
  const f = useRowFilter(data?.rows, needs);
  const [sel, setSel] = useState({});
  const [dlg, setDlg] = useState({ open: false, prefill: null });
  if (!data) return <Loading />;
  const chosen = f.filtered.filter((r) => sel[r.id] && r.net_to_order > 0);
  const allOn = f.filtered.length > 0 && f.filtered.every((r) => sel[r.id]);
  const toggleAll = () => setSel(allOn ? {} : Object.fromEntries(f.filtered.map((r) => [r.id, true])));
  const create = () => setDlg({ open: true, prefill: { kind: "manufacturing", items: chosen.map((r) => ({ product_id: r.id, qty: r.net_to_order, price: "" })) } });
  return (
    <div>
      <PageHeader title="Reorder & Manufacturing Plan" subtitle="Products whose stock is below REQ. STOCK 30D (30-day forecast × safety factor). Net to produce = Order qty on Sales & Forecast.">
        <MonthPicker month={month} setMonth={setMonth} />
        <ExportButton kind="reorder" params={{ month }} />
        <Button size="sm" disabled={!chosen.length || !data.is_current} onClick={create} data-testid="create-order-from-plan-btn"><Factory className="h-4 w-4" /> Create order ({chosen.length})</Button>
      </PageHeader>
      <PastMonthBanner data={data} />
      <FilterBar f={f} total={data.rows.filter(needs).length} />
      <div className="panel overflow-auto max-h-[calc(100vh-260px)]">
        <table className="data-table" data-testid="reorder-table">
          <thead><tr>
            <th className="w-8"><Checkbox checked={allOn} onCheckedChange={toggleAll} data-testid="reorder-select-all" /></th>
            <th>Product</th><th>Category</th><th className="text-right">{data.is_current ? "Current stock" : "Closing stock"}</th><th className="text-right">Avg daily</th>
            <th className="text-right">Required (30d)</th><th className="text-right">Reorder qty</th><th className="text-right">Pending orders</th>
            <th className="text-right">Net to produce</th><th>Movement</th>
          </tr></thead>
          <tbody>
            {f.filtered.map((r) => (
              <tr key={r.id} data-testid={`reorder-row-${r.sku}`}>
                <td><Checkbox checked={!!sel[r.id]} onCheckedChange={(v) => setSel((s) => ({ ...s, [r.id]: v }))} data-testid={`reorder-check-${r.sku}`} /></td>
                <td className="max-w-[320px]"><div className="truncate font-medium">{r.name}</div><div className="font-mono text-[10px] text-muted-foreground">{r.sku}</div></td>
                <td className="text-xs text-muted-foreground">{r.category}</td>
                <td className={`num ${r.current_stock <= 0 ? "text-red-400 font-bold" : ""}`}>{r.current_stock}</td>
                <td className="num text-muted-foreground" data-testid={`reorder-avg-daily-${r.sku}`}>{r.avg_daily.toFixed(2)}</td>
                <td className="num">{r.required}</td>
                <td className="num font-bold text-primary">{r.reorder_qty}</td>
                <td className="num text-sky-300">{r.pending_po || "—"}</td>
                <td className="num font-extrabold">{r.net_to_order}</td>
                <td><MovementBadge value={r.movement} /></td>
              </tr>
            ))}
            {!f.filtered.length && <tr><td colSpan={10} className="py-12 text-center text-muted-foreground">Nothing to reorder — all stock levels are healthy.</td></tr>}
          </tbody>
        </table>
      </div>
      <PurchaseOrderDialog open={dlg.open} prefill={dlg.prefill} onOpenChange={(o) => setDlg((s) => ({ ...s, open: o }))} />
    </div>
  );
}
